import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { ArrowLeft, Flag, Save, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';

export const UserDetail = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [score, setScore] = useState(null);
  const [banks, setBanks] = useState([]);
  const [override, setOverride] = useState('');
  const [flagged, setFlagged] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, [id]);

  const fetchAll = async () => {
    const [u, s, b] = await Promise.all([
      api.get(`/admin/users/${id}`),
      api.get(`/admin/users/${id}/score`),
      api.get(`/admin/users/${id}/bank-accounts`),
    ]);
    setUser(u.data.data?.user || u.data.data);
    const scoreData = s.data.data;
    setScore(scoreData);
    setOverride(scoreData.scoreOverride ?? '');
    setFlagged(scoreData.flagged);
    setFlagReason(scoreData.flagReason || '');
    setBanks(b.data.data?.bankAccounts || []);
  };

  const saveScore = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/users/${id}/score`, {
        scoreOverride: override === '' ? null : Number(override),
        flagged,
        flagReason: flagged ? flagReason : null,
      });
      await fetchAll();
      alert('Saved');
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error?.message || e.message));
    } finally { setSaving(false); }
  };

  if (!user || !score) return <div className="loader-container"><div className="spinner"></div></div>;

  return (
    <div>
      <Link to="/users" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-secondary)', textDecoration: 'none', marginBottom: 16 }}>
        <ArrowLeft size={14} /> Back to users
      </Link>
      <h1 className="page-title">{user.firstName} {user.lastName}</h1>
      <div style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>{user.email}</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {/* Score panel */}
        <div className="glass" style={{ padding: 24 }}>
          <h3 style={{ marginTop: 0 }}>CiruScore</h3>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 48, fontWeight: 700, color: score.effectiveScore < 500 ? '#ef4444' : score.effectiveScore < 700 ? '#f59e0b' : '#10b981' }}>
              {score.effectiveScore}
            </div>
            {score.scoreOverride !== null && (
              <span style={{ padding: '4px 8px', borderRadius: 6, background: 'rgba(245,158,11,0.2)', color: '#fcd34d', fontSize: 11 }}>
                OVERRIDE (computed: {score.computedScore})
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
            <div>Total contributed<br /><strong style={{ color: 'var(--text)', fontSize: 16 }}>${score.breakdown.totalContributed.toLocaleString()}</strong></div>
            <div>Total payouts received<br /><strong style={{ color: 'var(--text)', fontSize: 16 }}>${score.breakdown.totalPayouts.toLocaleString()}</strong></div>
            <div>Missed payments<br /><strong style={{ color: 'var(--text)', fontSize: 16 }}>{score.breakdown.missedPayments}</strong></div>
            <div>Late fees<br /><strong style={{ color: 'var(--text)', fontSize: 16 }}>${score.breakdown.lateFees.toLocaleString()}</strong></div>
            <div>Successful circles<br /><strong style={{ color: 'var(--text)', fontSize: 16 }}>{score.breakdown.successfulCircles}</strong></div>
            <div>Defaulted circles<br /><strong style={{ color: 'var(--text)', fontSize: 16 }}>{score.breakdown.defaultedCircles}</strong></div>
          </div>
        </div>

        {/* Admin override panel */}
        <div className="glass" style={{ padding: 24 }}>
          <h3 style={{ marginTop: 0 }}>Admin Override</h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>Override score (0-1000, blank to clear)</label>
            <input type="number" min="0" max="1000" value={override} onChange={e => setOverride(e.target.value)}
                   className="search-input" style={{ width: '100%' }} placeholder={String(score.computedScore)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={flagged} onChange={e => setFlagged(e.target.checked)} />
              <Flag size={16} color={flagged ? '#ef4444' : undefined} />
              Flag as suspicious
            </label>
          </div>
          {flagged && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6 }}>Flag reason</label>
              <input type="text" value={flagReason} onChange={e => setFlagReason(e.target.value)}
                     className="search-input" style={{ width: '100%' }} placeholder="Why is this user flagged?" />
            </div>
          )}
          <button className="btn-primary" onClick={saveScore} disabled={saving}
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Override'}
          </button>
        </div>
      </div>

      {/* Bank accounts */}
      <div className="glass" style={{ padding: 24, marginBottom: 24 }}>
        <h3 style={{ marginTop: 0 }}>Linked Bank Accounts</h3>
        {banks.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)' }}>No bank accounts linked</div>
        ) : (
          <div className="table-container" style={{ border: 'none' }}>
            <table>
              <thead><tr><th>Institution</th><th>Account</th><th>Type</th><th>Balance</th><th>Verified</th><th>Linked</th></tr></thead>
              <tbody>
                {banks.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 500 }}>{b.institutionName || '—'}</td>
                    <td>{b.accountName} •••• {b.accountMask}</td>
                    <td>{b.accountSubtype || b.accountType}</td>
                    <td>${Number(b.availableBalance || 0).toLocaleString()}</td>
                    <td>{b.ownershipVerified ? <span className="badge approved">YES</span> : <span className="badge pending">NO</span>}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{b.createdAt ? format(new Date(b.createdAt), 'MMM dd, yyyy') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
